
const API_URL = import.meta.env.VITE_API_URL ?? '/api'

const AUTH_PATHS = ['/auth/admin/login', '/auth/web/signup', '/auth/logout']

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public errors?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Shape mínima del body del 401 cuando el servidor revocó el token con
 * motivo (SDD revocacion-con-motivo, spec sección B). `motivo`/`revocado_at`
 * ausentes → 401 default de Laravel (logout voluntario, token basura, sin
 * bearer) → el cliente NO debe mostrar nada; el silencio es un requisito.
 */
interface RevocacionBody {
  message?: string
  motivo?: string
  revocado_at?: string
}

/**
 * fc-admin conserva la sesión única de `adminLogin()` (`admin-%` se revoca
 * a propósito — un puñado de super-admins, cualquier desalojo es sospechoso
 * y el compromiso alcanza a todos los tenants) → este cliente SÍ puede
 * recibir ambos motivos. Cualquier motivo no listado acá (uno nuevo a
 * futuro) cae al `message` genérico que ya manda el servidor.
 */
const COPY_REVOCACION: Partial<Record<string, (fechaHora: string) => string>> = {
  sesion_desplazada: (fechaHora) =>
    `Tu sesión se cerró: iniciaste sesión en otro dispositivo el ${fechaHora}. Si no fuiste tú, cambia tu contraseña.`,
  contrasena_cambiada: (fechaHora) =>
    `Tu sesión se cerró: tu contraseña fue actualizada el ${fechaHora}. Si no fuiste tú, contacta a soporte.`,
}

function formatearFechaHoraLocal(revocadoAt: string): string {
  const fecha = new Date(revocadoAt)
  const dia = fecha.toLocaleDateString('es-CL')
  const hora = fecha.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  return `${dia} a las ${hora}`
}

/** Parsea el body del 401 sin lanzar — algunas respuestas 401 pueden no traer JSON. */
async function parseRevocacionBody(res: Response): Promise<RevocacionBody | null> {
  try {
    return (await res.json()) as RevocacionBody
  } catch {
    return null
  }
}

/**
 * REQ-ADMIN-1: si el body trae `motivo`, deja el mensaje (tuteo) en
 * sessionStorage para que `LoginPage` lo muestre al montar. Sin `motivo` →
 * silencio total, comportamiento idéntico al actual.
 *
 * NO se hace `toast.error()` acá: el logout redirige con `window.location.href`,
 * que es una navegación completa — el árbol de React se desmonta y sonner muere
 * antes de pintar. (fc-app sí puede toastear en el interceptor porque usa ruteo
 * SPA de Angular y nunca se desmonta.) Mostrarlo en /login además es mejor: el
 * mensaje aparece donde el usuario aterriza, no parpadeando durante la
 * navegación.
 *
 * DIVERGENCIA DELIBERADA: el token de fc-admin vive en `localStorage`, pero el
 * mensaje va en `sessionStorage` — NO lo "normalices" a localStorage. El
 * desalojo lo detecta cada pestaña por su cuenta (todas comparten el token
 * muerto), así que sessionStorage hace que cada una muestre su propio mensaje
 * una sola vez. Con localStorage habría carrera: la primera pestaña en montar
 * consume el mensaje y las demás se quedan mudas.
 */
export const LOGOUT_MENSAJE_KEY = 'fc_logout_mensaje'

function stashMensajeSiRevocado(body: RevocacionBody | null): void {
  if (!body?.motivo) return

  const construirMensaje = COPY_REVOCACION[body.motivo]
  const mensaje =
    construirMensaje && body.revocado_at
      ? construirMensaje(formatearFechaHoraLocal(body.revocado_at))
      : body.message

  if (mensaje) sessionStorage.setItem(LOGOUT_MENSAJE_KEY, mensaje)
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token')

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const res = await fetch(`${API_URL}${path}`, { ...options, headers })

  if (res.status === 204) return undefined as T

  if (res.status === 401 && !AUTH_PATHS.includes(path)) {
    stashMensajeSiRevocado(await parseRevocacionBody(res))
    localStorage.removeItem('auth_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
    window.location.href = '/login'
    return undefined as T
  }

  const data = await res.json()

  if (!res.ok) {
    throw new ApiError(res.status, data.message ?? 'Error inesperado', data.errors)
  }

  return data as T
}

async function requestBlob(path: string): Promise<Blob> {
  const token = localStorage.getItem('auth_token')
  const headers: HeadersInit = {
    Accept: 'application/pdf, application/octet-stream',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
  const res = await fetch(`${API_URL}${path}`, { headers })

  if (res.status === 401) {
    stashMensajeSiRevocado(await parseRevocacionBody(res))
    localStorage.removeItem('auth_token')
    window.dispatchEvent(new CustomEvent('auth:logout'))
    window.location.href = '/login'
    throw new ApiError(401, 'Sesión expirada')
  }
  if (!res.ok) {
    throw new ApiError(res.status, `No se pudo descargar el archivo (${res.status})`)
  }

  return res.blob()
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  blob: (path: string) => requestBlob(path),
}

export { ApiError }
